import { X } from "lucide-react";
import {
  formatMessengerMonth,
  messengerBLabel,
  messengerClassLabel,
  messengerPersonaLabel,
} from "@/lib/messengerStoryLabels";
import { Button } from "@/components/ui/button";

export type ActiveFilterChip = {
  key: string;
  label: string;
  onRemove: () => void;
};

type Props = {
  chips: ActiveFilterChip[];
  onClearAll: () => void;
};

export default function MessengerActiveFilters({ chips, onClearAll }: Props) {
  if (chips.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <span className="text-xs font-medium text-slate-500">Filtry:</span>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
        >
          {chip.label}
          <X className="h-3 w-3 text-slate-400" />
        </button>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-slate-500"
        onClick={onClearAll}
      >
        Wyczyść wszystko
      </Button>
    </div>
  );
}

export function buildActiveFilterChips(
  filters: {
    q: string;
    storyClass?: string;
    persona?: string;
    theme?: string;
    bSignal?: string;
    month?: string;
    hasPurchase?: boolean;
    tier?: "story" | "voice";
  },
  handlers: {
    clearQ: () => void;
    clearClass: () => void;
    clearPersona: () => void;
    clearTheme: () => void;
    clearBSignal: () => void;
    clearMonth: () => void;
    clearPurchase: () => void;
    clearTier: () => void;
  },
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  if (filters.q.trim()) {
    chips.push({
      key: "q",
      label: `Szukaj: „${filters.q.trim().slice(0, 40)}”`,
      onRemove: handlers.clearQ,
    });
  }
  if (filters.tier === "voice") {
    chips.push({
      key: "tier",
      label: "Opinie i głos",
      onRemove: handlers.clearTier,
    });
  } else if (filters.tier === "story") {
    chips.push({
      key: "tier",
      label: "Tylko historie",
      onRemove: handlers.clearTier,
    });
  }
  if (filters.storyClass) {
    chips.push({
      key: "class",
      label: messengerClassLabel(filters.storyClass),
      onRemove: handlers.clearClass,
    });
  }
  if (filters.persona) {
    chips.push({
      key: "persona",
      label: messengerPersonaLabel(filters.persona),
      onRemove: handlers.clearPersona,
    });
  }
  if (filters.theme) {
    chips.push({
      key: "theme",
      label:
        filters.theme === "none"
          ? "Bez B-sygnału"
          : messengerBLabel(filters.theme),
      onRemove: handlers.clearTheme,
    });
  }
  if (filters.bSignal) {
    chips.push({
      key: "b_signal",
      label: messengerBLabel(filters.bSignal),
      onRemove: handlers.clearBSignal,
    });
  }
  if (filters.month) {
    chips.push({
      key: "month",
      label: formatMessengerMonth(filters.month),
      onRemove: handlers.clearMonth,
    });
  }
  if (filters.hasPurchase) {
    chips.push({
      key: "purchase",
      label: "Już kupiły",
      onRemove: handlers.clearPurchase,
    });
  }
  return chips;
}
